import {
  ConfirmDeleteDialog,
  OrganizationSearchAndSelect,
  SearchInputAndButton,
} from '@assembly/components'
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Pagination,
  Typography,
} from '@mui/material'
import React from 'react'
import CreateAssemblyLineDialog from './CreateAssemblyLineDialog'
import {
  deleteAssemblyLine,
  getAssemblyLines,
} from '@assembly/api/assemblyLine'
import { useAppStore, useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import AssemblyLinesList from './AssemblyLinesList'
import { AssemblyLine, AssemblyLineStatus, AssemblyLineQuery } from './types'
import { Organization } from '../OrganizationManager/types'
import { styled } from '@mui/material/styles'
import { capitalize, debounce } from 'lodash'

const OrganizationSearchAndSelectBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: 1,
  gap: theme.spacing(1),
  width: '50%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}))

const AssemblyLines: React.FC = () => {
  const [createAssemblyLineDialogOpen, setCreateAssemblyLineDialogOpen] =
    React.useState<boolean>(false)
  const [
    confirmDeleteAssemblyLineDialogOpen,
    setConfirmDeleteAssemblyLineDialogOpen,
  ] = React.useState<boolean>(false)
  const [selectedAssemblyLine, setSelectedAssemblyLine] = React.useState<{
    assemblyLine: AssemblyLine | undefined
    index: number
  }>({
    assemblyLine: undefined,
    index: -1,
  })
  const didMount = React.useRef(false)
  const { addAlert } = useSnackbars()
  const { store, setAssemblyLines } = useAppStore()
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false)
  const [deleteInputValue, setDeleteInputValue] = React.useState<string>('')
  const [selectedOrganization, setSelectedOrganization] = React.useState<
    Organization | undefined
  >()
  const [isSeachLoading, setIsSearchLoading] = React.useState<boolean>(false)
  const [searchValue, setSearchValue] = React.useState<string>('')
  const [status, setSatatus] = React.useState<AssemblyLineStatus | ''>('')

  // Pagination states
  const [currentPage, setCurrentPage] = React.useState<number>(0)
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [totalPages, setTotalPages] = React.useState<number>(0)
  const [totalItems, setTotalItems] = React.useState<number>(0)

  React.useEffect(() => {
    if (!didMount.current) {
      getAssemblyLinesAsync()
      didMount.current = true
    }
  }, [])

  const getAssemblyLinesAsync = async () => {
    try {
      setIsLoading(true)
      const query: AssemblyLineQuery = {
        search: '',
        orgID: selectedOrganization?.id || '',
        status: status || undefined,
        limit: pageSize,
        offset: currentPage * pageSize,
      }
      const { data } = await getAssemblyLines(query)
      setAssemblyLines(data.data)
      setTotalPages(data.totalPages || 0)
      setTotalItems(data.totalCount || 0)
      setIsLoading(false)
    } catch (error) {
      console.error(error)
      addAlert({
        message: 'Error: Failed to load assembly lines',
        type: SnackbarType.Error,
      })
      setIsLoading(false)
    }
  }

  const searchAssemblyLines = async (
    search: string,
    orgID: string,
    status: AssemblyLineStatus | '',
    page: number = currentPage
  ) => {
    try {
      setIsSearchLoading(true)
      const query: AssemblyLineQuery = {
        search,
        orgID,
        status: status || undefined,
        limit: pageSize,
        offset: page * pageSize,
      }

      const { data } = await getAssemblyLines(query)
      setAssemblyLines(data.data)
      setTotalPages(data.totalPages || 0)
      setTotalItems(data.totalCount || 0)
      setCurrentPage(page)
      setIsSearchLoading(false)
    } catch (error) {
      console.error(error)
      setIsSearchLoading(false)
      addAlert({
        message: 'Error: Failed to search assembly lines',
        type: SnackbarType.Error,
      })
    }
  }

  const handleClickDelete = (assemblyLine: AssemblyLine, index: number) => {
    setSelectedAssemblyLine({ assemblyLine, index })
    setConfirmDeleteAssemblyLineDialogOpen(true)
  }

  const handleClickEdit = (assemblyLine: AssemblyLine, index: number) => {
    setSelectedAssemblyLine({ assemblyLine, index })
    setCreateAssemblyLineDialogOpen(true)
  }

  const handleCloseAssemblyLineDialog = () => {
    setSelectedAssemblyLine({ assemblyLine: undefined, index: -1 })
    setCreateAssemblyLineDialogOpen(false)
  }

  const onEdit = (assemblyLine: AssemblyLine) => {
    store.assemblyLines[selectedAssemblyLine.index] = assemblyLine
    setAssemblyLines([...store.assemblyLines])
  }

  const onCreate = (assemblyLine: AssemblyLine) => {
    setAssemblyLines([assemblyLine, ...store.assemblyLines])
    setSelectedAssemblyLine({ assemblyLine, index: 0 })
  }

  const handleCloseConfirmDeleteDialog = (): void => {
    setConfirmDeleteAssemblyLineDialogOpen(false)
    setDeleteInputValue('')
    setSelectedAssemblyLine({ assemblyLine: undefined, index: -1 })
  }

  const handleChangeDeleteInputValue = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDeleteInputValue(e.target.value)
  }

  const removeFromList = () => {
    if (selectedAssemblyLine.index === -1) {
      return
    }

    store.assemblyLines.splice(selectedAssemblyLine.index, 1)
    setAssemblyLines([...store.assemblyLines])
  }

  const deleteAsync = async () => {
    if (!selectedAssemblyLine.assemblyLine) {
      return
    }

    setIsDeleting(true)

    try {
      await deleteAssemblyLine(selectedAssemblyLine.assemblyLine.id)
      setIsDeleting(false)
      setSelectedAssemblyLine({ assemblyLine: undefined, index: -1 })
      setDeleteInputValue('')
      removeFromList()
      addAlert({
        message: 'Assembly Line Deleted!',
        type: SnackbarType.Success,
      })
      setConfirmDeleteAssemblyLineDialogOpen(false)
    } catch (error) {
      console.error(error)
      setIsDeleting(false)
      addAlert({ message: 'Error', type: SnackbarType.Error })
    }
  }

  const handleChangeSearch = debounce((value) => {
    setSearchValue(value)
    setCurrentPage(0) // Reset to first page when searching
    searchAssemblyLines(value, selectedOrganization?.id || '', status, 0)
  }, 1000)

  const handleChangeOrganization = (organization: Organization) => {
    setSelectedOrganization(organization)
    setCurrentPage(0) // Reset to first page when changing organization
    searchAssemblyLines(searchValue, organization?.id || '', status, 0)
  }

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const { value } = event.target
    setSatatus(value as AssemblyLineStatus)
    setCurrentPage(0) // Reset to first page when changing status
    searchAssemblyLines(searchValue, selectedOrganization?.id || '', value as AssemblyLineStatus, 0)
  }

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    const pageIndex = page - 1 // Convert to 0-indexed
    setCurrentPage(pageIndex)
    searchAssemblyLines(searchValue, selectedOrganization?.id || '', status, pageIndex)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <SearchInputAndButton
        buttonLabel="Create Assembly Line"
        inputPlaceholder="Search Assembly Lines"
        onClickButton={() => setCreateAssemblyLineDialogOpen(true)}
        onChangeSearch={(value) => handleChangeSearch(value)}
      />
      <OrganizationSearchAndSelectBox>
        <OrganizationSearchAndSelect
          value={selectedOrganization || null}
          onChange={handleChangeOrganization}
          small
          hideLabel
        />
        <FormControl fullWidth size="small">
          <InputLabel id="status-select-label">Search By Status</InputLabel>
          <Select
            labelId="status-select-label"
            value={status}
            label={'Search by status'}
            name="status"
            onChange={handleChangeSelect}
            placeholder="Search by status"
          >
            {Object.values(AssemblyLineStatus).map((value) => (
              <MenuItem key={value} value={value}>
                {capitalize(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </OrganizationSearchAndSelectBox>

      {isSeachLoading ? (
        <Box display="flex" justifyContent="center" marginTop={2}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <AssemblyLinesList
            assemblyLines={store.assemblyLines}
            onClickDelete={handleClickDelete}
            onClickEdit={handleClickEdit}
          />
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" marginTop={3} gap={2}>
              <Typography variant="body2" color="text.secondary">
                Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalItems)} of {totalItems} assembly lines
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage + 1} // Convert to 1-indexed for MUI Pagination
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
      <CreateAssemblyLineDialog
        open={createAssemblyLineDialogOpen}
        onClose={handleCloseAssemblyLineDialog}
        onSuccess={selectedAssemblyLine.assemblyLine ? onEdit : onCreate}
        assemblyLine={selectedAssemblyLine.assemblyLine}
      />
      <ConfirmDeleteDialog
        open={confirmDeleteAssemblyLineDialogOpen}
        onClose={handleCloseConfirmDeleteDialog}
        name={selectedAssemblyLine.assemblyLine?.name || ''}
        onClickDelete={deleteAsync}
        isLoading={isDeleting}
        inputValue={deleteInputValue}
        onChangeInput={handleChangeDeleteInputValue}
      />
    </Box>
  )
}

export default AssemblyLines
